import { PasseriniAPI } from '@/services/apiService';
import type {
  ModoImportacionPlantilla,
  ParteItem,
  PdfExportado,
  PresupuestoProforma,
  PresupuestoProformaDatosFormData,
  PresupuestoProformaItem,
  PresupuestoProformaItemFormData,
  PresupuestosProformaFiltros,
  PresupuestosProformaListado,
  Plantilla,
  PlantillaFormData,
  PlantillaItem,
  PlantillaItemFormData,
} from '../types';
import { nombreArchivoDeDisposition } from '../utils/nombreArchivo';

const BASE = '/presupuestos';
const PLANTILLAS = '/presupuesto-plantillas';

export const presupuestoProformaService = {
  listar: async (filtros: PresupuestosProformaFiltros = {}): Promise<PresupuestosProformaListado> => {
    const response = await PasseriniAPI.get(BASE, { params: filtros });
    return response.data;
  },

  obtener: async (id: string, parte?: ParteItem): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.get(`${BASE}/${id}`, {
      params: parte ? { parte } : undefined,
    });
    return response.data;
  },

  crear: async (
    data: PresupuestoProformaDatosFormData & { nombre: string; plantilla_id?: string | null }
  ): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.post(BASE, data);
    return response.data;
  },

  actualizar: async (
    id: string,
    data: PresupuestoProformaDatosFormData
  ): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.put(`${BASE}/${id}`, data);
    return response.data;
  },

  eliminar: async (id: string): Promise<void> => {
    await PasseriniAPI.delete(`${BASE}/${id}`);
  },

  confirmar: async (id: string): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.post(`${BASE}/${id}/confirmar`);
    return response.data;
  },

  volverABorrador: async (id: string): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.post(`${BASE}/${id}/borrador`);
    return response.data;
  },

  archivar: async (id: string): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.post(`${BASE}/${id}/archivar`);
    return response.data;
  },

  desarchivar: async (id: string): Promise<PresupuestoProforma> => {
    const response = await PasseriniAPI.post(`${BASE}/${id}/desarchivar`);
    return response.data;
  },

  crearItem: async (
    presupuestoId: string,
    data: PresupuestoProformaItemFormData
  ): Promise<PresupuestoProformaItem> => {
    const response = await PasseriniAPI.post(`${BASE}/${presupuestoId}/items`, data);
    return response.data;
  },

  actualizarItem: async (
    presupuestoId: string,
    itemId: string,
    data: Partial<PresupuestoProformaItemFormData>
  ): Promise<PresupuestoProformaItem> => {
    const response = await PasseriniAPI.put(`${BASE}/${presupuestoId}/items/${itemId}`, data);
    return response.data;
  },

  eliminarItem: async (presupuestoId: string, itemId: string): Promise<void> => {
    await PasseriniAPI.delete(`${BASE}/${presupuestoId}/items/${itemId}`);
  },

  reordenarItems: async (presupuestoId: string, items: string[]): Promise<void> => {
    await PasseriniAPI.post(`${BASE}/${presupuestoId}/items/reordenar`, { items });
  },

  /**
   * Copia los ítems activos de una plantilla. Si el presupuesto ya tiene ítems y
   * no se indica `modo`, la API responde 409 pidiendo elegir agregar o reemplazar.
   */
  importarPlantilla: async (
    presupuestoId: string,
    plantillaId: string,
    modo?: ModoImportacionPlantilla
  ): Promise<PresupuestoProforma & { importados: number }> => {
    const response = await PasseriniAPI.post(`${BASE}/${presupuestoId}/importar-plantilla`, {
      plantilla_id: plantillaId,
      ...(modo ? { modo } : {}),
    });
    return response.data;
  },

  /**
   * Leyendas predeterminadas vigentes (Configuración del Sistema), para
   * restablecerlas en un presupuesto.
   */
  notasPredeterminadas: async (): Promise<string[]> => {
    const response = await PasseriniAPI.get(`${BASE}/notas-predeterminadas`);
    return response.data.notas;
  },

  /**
   * Descarga el PDF de una parte, con el nombre de archivo que arma el backend.
   */
  exportar: async (id: string, parte: ParteItem): Promise<PdfExportado> => {
    const response = await PasseriniAPI.get(`${BASE}/${id}/export`, {
      params: { parte },
      responseType: 'blob',
    });
    return {
      blob: response.data,
      nombreArchivo: nombreArchivoDeDisposition(response.headers['content-disposition']),
    };
  },
};

export const plantillaService = {
  listar: async (soloActivas = false): Promise<Plantilla[]> => {
    const response = await PasseriniAPI.get(PLANTILLAS, {
      params: soloActivas ? { activo: 1 } : undefined,
    });
    return response.data.plantillas;
  },

  obtener: async (id: string): Promise<Plantilla> => {
    const response = await PasseriniAPI.get(`${PLANTILLAS}/${id}`);
    return response.data;
  },

  crear: async (data: PlantillaFormData & { nombre: string }): Promise<Plantilla> => {
    const response = await PasseriniAPI.post(PLANTILLAS, data);
    return response.data;
  },

  actualizar: async (id: string, data: PlantillaFormData): Promise<Plantilla> => {
    const response = await PasseriniAPI.put(`${PLANTILLAS}/${id}`, data);
    return response.data;
  },

  eliminar: async (id: string): Promise<void> => {
    await PasseriniAPI.delete(`${PLANTILLAS}/${id}`);
  },

  /** Copia la plantilla con todos sus ítems. */
  duplicar: async (id: string): Promise<Plantilla> => {
    const response = await PasseriniAPI.post(`${PLANTILLAS}/${id}/duplicar`);
    return response.data;
  },

  reordenar: async (plantillas: string[]): Promise<void> => {
    await PasseriniAPI.post(`${PLANTILLAS}/reordenar`, { plantillas });
  },

  crearItem: async (plantillaId: string, data: PlantillaItemFormData): Promise<PlantillaItem> => {
    const response = await PasseriniAPI.post(`${PLANTILLAS}/${plantillaId}/items`, data);
    return response.data;
  },

  actualizarItem: async (
    plantillaId: string,
    itemId: string,
    data: PlantillaItemFormData
  ): Promise<PlantillaItem> => {
    const response = await PasseriniAPI.put(`${PLANTILLAS}/${plantillaId}/items/${itemId}`, data);
    return response.data;
  },

  eliminarItem: async (plantillaId: string, itemId: string): Promise<void> => {
    await PasseriniAPI.delete(`${PLANTILLAS}/${plantillaId}/items/${itemId}`);
  },

  reordenarItems: async (plantillaId: string, items: string[]): Promise<void> => {
    await PasseriniAPI.post(`${PLANTILLAS}/${plantillaId}/items/reordenar`, { items });
  },
};
